// Default config and business shapes (API-only; no mock persistence).
// DB pattern: null = use product/business/calendar resolution defaults until the user sets an explicit value.
//
// DEFAULT GREETING CONTENT IS LOCALIZED:
// Welcome message, welcome buttons, floating button text and fallback business name are translated
// (`web_ai_widget.defaults.*` in web-ai-widget-pov.*.yml) because the public widget serves them in
// the business's language (webaiwidgetmgr `web_ai_widget.*` in src/config/locales). Keep the
// English copy of the two in sync. Pass the component's `$t` to the getters below.

/**
 * Store-level placeholder business (mock id + persisted fallback name). Its `name` stays English
 * because the store may use it before the module's translations load; anything rendered should
 * use getDefaultBusinessName($t) instead.
 */
const DEFAULT_BUSINESS = {
  id: 'mock-business-1',
  name: 'My Business',
};

/** Default welcome buttons, in display order (keys under `web_ai_widget.defaults.welcome_buttons`). */
const DEFAULT_WELCOME_BUTTON_KEYS = ['book_appointment', 'ask_about_services', 'opening_hours'];

/**
 * Business name shown when the platform name is unavailable.
 * @param {Function} t - The component's `$t`
 * @returns {string}
 */
function getDefaultBusinessName(t) {
  return t('web_ai_widget.defaults.business_name');
}

/**
 * Build the default welcome message with business name inserted.
 * @param {Function} t - The component's `$t`
 * @param {string} businessName - Business display name (e.g. from platform API)
 * @returns {string}
 */
function getDefaultWelcomeMessage(t, businessName) {
  const name = (businessName || '').trim() || getDefaultBusinessName(t);
  return t('web_ai_widget.defaults.welcome_message', { business_name: name });
}

/**
 * Default welcome buttons shown under the welcome message (fresh objects, safe to mutate).
 * @param {Function} t - The component's `$t`
 * @returns {{ label: string, message: string }[]}
 */
function getDefaultWelcomeButtons(t) {
  return DEFAULT_WELCOME_BUTTON_KEYS.map((key) => ({
    label: t(`web_ai_widget.defaults.welcome_buttons.${key}.label`),
    message: t(`web_ai_widget.defaults.welcome_buttons.${key}.message`),
  }));
}

/**
 * Lead intake defaults. `lead_basic_contact_fields` enum value must stay in sync with the backend
 * default (webaiwidgetserver `DEFAULT_LEAD_BASIC_CONTACT_FIELDS`). These are config values, not i18n.
 */
const DEFAULT_LEAD_BASIC_CONTACT_FIELDS = 'phone_or_email';
const LEAD_BASIC_CONTACT_FIELDS_OPTIONS = ['phone_or_email', 'phone', 'email', 'both'];
const MAX_LEAD_FIELDS = 10;
const LEAD_FIELD_LABEL_MAX = 100;
const LEAD_FIELD_INSTRUCTIONS_MAX = 200;

/**
 * Floating button label when `button_text` is null in DB.
 * @param {Function} t - The component's `$t`
 * @returns {string}
 */
export function getDefaultButtonText(t) {
  return t('web_ai_widget.defaults.button_text');
}

/** Picker fallback when business brand color is unavailable. */
export const DEFAULT_BRAND_COLOR_FALLBACK = '#3B82F6';

/**
 * Legacy neutral gray; **default text on brand** uses portal parity via `contrastTextColor`
 * (Core `ColorBrightness` / `brand_text_contrast`), not this constant.
 */
export const DEFAULT_TEXT_COLOR = '#333333';

/** Default auto-popup delay (seconds) when `auto_popup_delay` is null in DB. */
export const DEFAULT_AUTO_POPUP_DELAY_SECONDS = 5;

/**
 * In-memory / first-persist shape: optional fields are null so the API stores NULL until customized.
 */
const DEFAULT_CONFIG = {
  id: '',
  business_id: '',
  widget_title: null,
  welcome_message: null,
  brand_color: null,
  text_color: null,
  logo_url: null,
  actions: null,
  auto_popup_enabled: null,
  auto_popup_delay: null,
  welcome_buttons: null,
  lead_basic_contact_fields: null,
  lead_fields: null,
  button_text: null,
  include_business_hours: null,
  include_contact_info: null,
  include_services: null,
  include_products: null,
  include_booking_policies: null,
  include_terms_of_service: null,
  allow_chat_based_online_scheduling: null,
  guidance_urls: null,
  included_business_rule_categories: null,
};

/**
 * Paths to edit guidance sources in the app (Angular/settings). Used for "edit" links next to checkboxes.
 * Values can be a path string or { path, queryParams } so Angular receives query params separately and
 * can merge them (e.g. with section=website-widgets) without producing a malformed URL.
 */
const GUIDANCE_EDIT_PATHS = {
  contact_info: '/app/settings/business',
  services: '/app/settings/services',
  products: '/app/settings/products',
  booking_policies: { path: '/app/settings/services', queryParams: { tab: 'settings', active_tab: 1 } },
  terms_of_service: { path: '/app/settings/services', queryParams: { tab: 'settings', active_tab: 1 } },
};

/** Keys for the guidance inclusion checkboxes (Content tab). */
const GUIDANCE_CHECKBOX_KEYS = [
  'include_business_hours',
  'include_contact_info',
  'include_services',
  'include_products',
  'include_booking_policies',
  'include_terms_of_service',
];

/**
 * User-facing AI business-rule categories shown in the Knowledge tab inclusion list,
 * in canonical order. Mirrors the webaiwidgetserver allowlist (client_facing_bot_policies
 * is intentionally excluded). `linkKey` resolves the category label / edit-link text.
 */
const BUSINESS_RULE_CATEGORIES = [
  { code: 'tone_of_voice', linkKey: 'business_rule_link_tone_of_voice' },
  { code: 'policies_and_info', linkKey: 'business_rule_link_policies_and_info' },
  { code: 'things_to_avoid', linkKey: 'business_rule_link_things_to_avoid' },
  { code: 'faq', linkKey: 'business_rule_link_faq' },
  { code: 'lead_handling', linkKey: 'business_rule_link_lead_handling' },
];

const BUSINESS_RULE_CATEGORY_CODES = BUSINESS_RULE_CATEGORIES.map((c) => c.code);

/** POV route + tab for editing a business-rule category (bizai settings). */
const BUSINESS_RULES_EDIT_ROUTE = { name: 'bizai-settings', path: '/settings/bizai', tab: 'business_rules' };

/** Calendar settings → General availability (same as Vuetage `navigateTo('app.calendar_settings', { tab: 'GeneralAvailability' })`). */
const CALENDAR_GENERAL_AVAILABILITY_SETTINGS = {
  path: '/app/settings/calendar_settings',
  queryParams: { tab: 'GeneralAvailability' },
};

/**
 * Build the full HTML page for the widget preview iframe.
 * Kept here to keep the preview component focused on layout/lifecycle.
 * @param {object} opts
 * @param {string} opts.websiteUrl - Normalised website URL (or '' for placeholder)
 * @param {string} opts.widgetId
 * @param {string} opts.chatServiceUrl
 * @param {string} opts.scriptUrl - Widget JS entry point
 * @param {string} opts.desktopCssUrl
 * @param {string} opts.mobileCssUrl
 * @param {string} opts.widgetMode - 'desktop' | 'mobile'
 * @param {boolean} opts.widgetOpen - When true the chat pane is forced open on load (preview only)
 * @param {boolean} opts.showSkeleton - When true and no real website is loaded, show the skeleton
 *   website backdrop (pop-up preview). When false, fall back to a clear background (customization preview).
 * @returns {string} Complete HTML document
 */
function buildPreviewPageHtml({
  websiteUrl, widgetId, chatServiceUrl, scriptUrl,
  desktopCssUrl, mobileCssUrl, widgetMode, sourceUrl, widgetOpen, showSkeleton = true,
}) {
  const w = JSON.stringify(websiteUrl);
  const wi = JSON.stringify(widgetId);
  const c = JSON.stringify(chatServiceUrl);
  const s = JSON.stringify(scriptUrl);
  const dCss = JSON.stringify(desktopCssUrl);
  const mCss = JSON.stringify(mobileCssUrl);
  const mode = JSON.stringify(widgetMode);
  const source = JSON.stringify(sourceUrl || '');
  const open = JSON.stringify(!!widgetOpen);
  const skeleton = JSON.stringify(!!showSkeleton);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Widget Preview</title>
<style>
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; overflow: hidden; height: 100%; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
#preview-background { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; display: block; z-index: 0; }
/* Skeleton website backdrop (shown when no real website URL is set). */
#preview-skeleton { position: absolute; inset: 0; display: flex; flex-direction: column; background: #fff; padding: 28px 32px; z-index: 0; }
#preview-skeleton .sk-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 28px; }
#preview-skeleton .sk-logo { height: 16px; width: 96px; background: #e8eaed; border-radius: 6px; }
#preview-skeleton .sk-nav { display: flex; gap: 18px; }
#preview-skeleton .sk-nav-item { height: 10px; width: 44px; background: #eef0f2; border-radius: 6px; }
#preview-skeleton .sk-main { display: flex; gap: 28px; flex: 1; min-height: 0; }
#preview-skeleton .sk-left { flex: 1; display: flex; flex-direction: column; gap: 20px; }
#preview-skeleton .sk-hero { flex: 1; background: #f3f4f6; border-radius: 14px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; }
#preview-skeleton .sk-hero-title { height: 16px; width: 200px; background: #e2e5e9; border-radius: 7px; }
#preview-skeleton .sk-hero-subtitle { height: 11px; width: 150px; background: #eaedf0; border-radius: 6px; }
#preview-skeleton .sk-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
#preview-skeleton .sk-card { height: 84px; background: #f3f4f6; border-radius: 12px; }
#preview-skeleton .sk-side { width: 220px; }
</style>
</head>
<body>
<div id="preview-skeleton">
  <div class="sk-header">
    <span class="sk-logo"></span>
    <div class="sk-nav">
      <span class="sk-nav-item"></span>
      <span class="sk-nav-item"></span>
      <span class="sk-nav-item"></span>
      <span class="sk-nav-item"></span>
    </div>
  </div>
  <div class="sk-main">
    <div class="sk-left">
      <div class="sk-hero">
        <span class="sk-hero-title"></span>
        <span class="sk-hero-subtitle"></span>
      </div>
      <div class="sk-cards">
        <span class="sk-card"></span>
        <span class="sk-card"></span>
        <span class="sk-card"></span>
      </div>
    </div>
    <div class="sk-side"></div>
  </div>
</div>
<iframe id="preview-background" title="Website background" sandbox="allow-scripts allow-same-origin allow-forms allow-popups"></iframe>
<script>
(function() {
  const websiteUrl = ${w};
  const showSkeleton = ${skeleton};
  const skeleton = document.getElementById('preview-skeleton');
  const bg = document.getElementById('preview-background');
  let siteUrl = '';
  if (websiteUrl) {
    try {
      const u = new URL(websiteUrl);
      if (u.protocol === 'https:' || u.protocol === 'http:') {
        const host = (u.hostname || '').toLowerCase();
        const sameOrigin = host === window.location.hostname.toLowerCase();
        if (!sameOrigin) { siteUrl = websiteUrl; }
      }
    } catch (e) {}
  }
  const showBackdrop = function() {
    skeleton.style.display = showSkeleton ? 'flex' : 'none';
    bg.style.display = 'none';
  };
  const showSite = function() {
    skeleton.style.display = 'none';
    bg.style.display = 'block';
  };
  if (siteUrl) {
    // Keep the skeleton (or clear background) until the website iframe finishes loading,
    // then swap to the real site. A timeout guards against the load event never firing.
    showBackdrop();
    let revealed = false;
    const reveal = function() { if (revealed) return; revealed = true; showSite(); };
    const SITE_LOAD_TIMEOUT_MS = 8000;
    bg.addEventListener('load', reveal);
    setTimeout(reveal, SITE_LOAD_TIMEOUT_MS);
    bg.src = siteUrl;
  } else {
    // No loadable external site: show the skeleton backdrop (pop-up) or a clear background.
    showBackdrop();
  }
  const widgetId = ${wi};
  const chatServiceUrl = ${c};
  const scriptUrl = ${s};
  const desktopCssUrl = ${dCss};
  const mobileCssUrl = ${mCss};
  const cacheBuster = Date.now();
  const bust = function(url) { return url ? url + (url.indexOf('?') !== -1 ? '&' : '?') + '_t=' + cacheBuster : ''; };
  if (widgetId && chatServiceUrl && scriptUrl) {
    const initOpts = { id: widgetId, chatServiceUrl: chatServiceUrl, configCacheBuster: cacheBuster, fromWidgetEditor: true, mode: ${mode}, source: ${source} };
    // Start from a clean state rather than restoring persisted state. The pop-up preview
    // forces the chat pane open; otherwise the widget auto-opens after its configured delay.
    initOpts.reset = true;
    if (${open}) initOpts.engageState = 'opened';
    if (desktopCssUrl) initOpts.desktopCss = bust(desktopCssUrl);
    if (mobileCssUrl) initOpts.mobileCss = bust(mobileCssUrl);
    window.liveSiteAsyncInit = function() { LiveSite.init(initOpts); };
    const el = document.createElement('script');
    el.src = bust(scriptUrl);
    el.async = true;
    document.body.appendChild(el);
  }
})();
\x3c/script>
</body>
</html>`;
}

export {
  DEFAULT_CONFIG,
  DEFAULT_LEAD_BASIC_CONTACT_FIELDS,
  LEAD_BASIC_CONTACT_FIELDS_OPTIONS,
  MAX_LEAD_FIELDS,
  LEAD_FIELD_LABEL_MAX,
  LEAD_FIELD_INSTRUCTIONS_MAX,
  DEFAULT_BUSINESS,
  getDefaultBusinessName,
  getDefaultWelcomeMessage,
  getDefaultWelcomeButtons,
  GUIDANCE_EDIT_PATHS,
  GUIDANCE_CHECKBOX_KEYS,
  BUSINESS_RULE_CATEGORIES,
  BUSINESS_RULE_CATEGORY_CODES,
  BUSINESS_RULES_EDIT_ROUTE,
  CALENDAR_GENERAL_AVAILABILITY_SETTINGS,
  buildPreviewPageHtml,
};
